import { ObjectiveUnitListModel, VCSLevelModel } from "@/models/title-list-model";
import { MessageHelper, NotificationType, ServerExceptionCode, fmtMsg } from "commonui";
import { isNull, isUndefined, sortBy, uniq } from "lodash";
import { Tag, Icon, Select, Input, Form, Button, Modal, Table, Popover, Checkbox } from "ant-design-vue";
import { DegreeType, ObjectiveActionType } from "@/util/enum";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import PageTitle from "@/components/page-title/page-title.vue";
import {
	ObjectiveService,
	ITreeNode,
	UnitObjectiveModel,
	UnitobjectiverelatedModel,
	RelatedContentModel
} from "@/services/objective-manager";
import { VersionGroupModel } from "@/services/version-group";
import { CommonLocale, VcsLocale } from "@/locales/localeid";
import { ObjectiveModule } from "@/modules/objective";
import Loading from "@/components/loading/loading.vue";
import RichTextSingleLineEditor from "@/components/rich-text-single-line-editor/rich-text-single-line-editor.vue";
import { pureRichText } from "@/util/func";

interface CheckboxOptionsModel {
	label: string;
	value: any;
}

@Component({
	components: {
		Tag,
		Icon,
		Form,
		Input,
		Table,
		Select,
		Button,
		Popover,
		Loading,
		PageTitle,
		Item: Form.Item,
		Option: Select.Option,
		RichTextSingleLineEditor,
		TextArea: Input.TextArea,
		CheckBoxGroup: Checkbox.Group
	}
})
export default class UnitManage extends Vue {
	@Prop({ default: false }) visible!: boolean;
	@Prop({ default: {} }) objective!: ITreeNode;
	@Prop({ default: [] }) treedata!: ITreeNode[];
	@Prop({ default: {} }) versionGroup!: VersionGroupModel;
	@Prop({ default: [] }) vcsLevelUnits!: VCSLevelModel[];
	public ObjectiveModule = ObjectiveModule;
	public unitManageForm;
	public isLoading = false;
	public unitOptions: VCSLevelModel[] = [];
	public contentObjectives: UnitobjectiverelatedModel[] = [];
	public unitObjectivs: UnitObjectiveModel[] = [];
	public relatedContents: RelatedContentModel[] = [];
	public DegreeType = DegreeType;
	public parentUnitOptions: CheckboxOptionsModel[] | null = [];
	public selectedUnitOptions: number[] = [];
	public deleteMsg = "";
	public canNotDelete = false;
	public currentDegreeType: any = null;
	public ObjectiveActionType = ObjectiveActionType;
	public objectiveInsertPosition: number = this.maxPosition;
	public objectiveEditPosition: number = this.currentPosition;
	public objectiveInComponents = false;
	public objectiveUnitList: ObjectiveUnitListModel[] = [];
	public filteredContentObjectives: any = [];
	public isLevel3Warning: boolean = false;
	public enableSaveButton: boolean = false;

	public rules = [
		{ required: true, message: fmtMsg(CommonLocale.CommonFieldRequired, { name: "text" }) },
		{
			max: 500,
			message: fmtMsg(CommonLocale.CommonFieldLength, { name: "text", value: "500" }),
			trigger: "change"
		},
		{ validator: this.validateTitle, trigger: "change" }
	];

	public richTextRules = [
		{ required: true, message: fmtMsg(CommonLocale.CommonFieldRequired, { name: "text" }) },
		{
			max: 500,
			message: fmtMsg(CommonLocale.CommonFieldLength, { name: "text", value: "500" }),
			trigger: "change"
		},
		{ validator: this.validateInputCustomText, trigger: "change" }
	];

	public columns: any = [
		{
			title: "Unit",
			dataIndex: "unitId",
			key: "unitId",
			customRender: (text, record, index) => {
				return record.unit.unit;
			}
		},
		{
			title: "Count",
			dataIndex: "contents",
			key: "contents",
			scopedSlots: { customRender: "contents" }
		}
	];

	public columnsContents: any = [
		{
			title: "Unit",
			dataIndex: "id",
			key: "id",
			customRender: (text, record, index) => {
				return record.vcsLevel.unit;
			}
		},
		{
			title: "Content",
			dataIndex: "title",
			key: "title"
		}
	];

	created() {
		if (!this.unitManageForm) {
			this.unitManageForm = this.$form.createForm(this, { name: "unitManageForm" });
		}
	}

	mounted(): void {}

	get filterDegreeType(): any {
		return Object.values(DegreeType).filter((d) => !isNaN(d as any));
	}

	get filterVcsUnits() {
		return this.vcsLevelUnits.filter((u) => u.curriculumType == this.versionGroup.curriculumType);
	}

	@Watch("ObjectiveModule.parentunitobjectiverelated")
	cloneParentUnitObjectives(parentunitobjectiverelated: UnitobjectiverelatedModel[]) {
		const options = parentunitobjectiverelated
			.filter((u) => u.unit.curriculumType === this.versionGroup.curriculumType)
			.map((u) => {
				const option: CheckboxOptionsModel = {
					label: u.unit.displayName,
					value: u.unitId
				};

				return option;
			});

		this.parentUnitOptions = options;
	}

	@Watch("ObjectiveModule.ownunitobjectiverelated")
	cloneOwnUnitObjectives(ownunitobjectiverelated: UnitobjectiverelatedModel[]) {
		const units = ownunitobjectiverelated
			.filter((u) => u.unit.curriculumType === this.versionGroup.curriculumType)
			.map((u) => {
				return u.unit.id;
			});

		this.selectedUnitOptions = units;
	}

	@Watch("ObjectiveModule.unitobjectiverelated")
	cloneContentObjectives(unitobjectiverelated) {
		this.contentObjectives = unitobjectiverelated.filter((data) => data.count > 0);
	}

	@Watch("ObjectiveModule.unitobjectiverelated")
	cloneUnitObjectivs(unitobjectiverelated) {
		this.unitObjectivs = unitobjectiverelated.map((data) => {
			return {
				// unit: data.unit,
				id: data.id,
				unitId: data.unitId,
				degree: data.degree,
				objectiveId: data.objectiveId
			};
		});
	}

	@Watch("ObjectiveModule.relatedContents")
	cloneRelatedContents(relatedContents) {
		this.relatedContents = sortBy(relatedContents, "vcsLevel.unit");
	}

	@Watch("unitObjectivs")
	@Watch("currentDegreeType")
	formatUnits(): void {
		this.unitOptions = this.filterVcsUnits.concat();
		this.unitObjectivs.forEach((o) => {
			const index: number = this.unitOptions.findIndex((u) => u.id === o.unitId);
			if (index > -1) {
				this.unitOptions.splice(index, 1);
			}
		});
	}

	formatAssignUnit(unitId) {
		const options = this.filterVcsUnits.concat();
		const unitOption = options.find((u) => u.id == unitId);
		return unitOption ? unitOption.unit : unitId;
	}

	selectionVisible(type: DegreeType): boolean {
		return this.currentDegreeType === type;
	}

	filterUnits(type: DegreeType) {
		return sortBy(this.unitObjectivs, "unitId").filter((u) => u.degree === type);
	}

	onSelect(value: number): void {
		this.onEditObjective();
		this.unitObjectivs.push({
			degree: this.currentDegreeType,
			unitId: value,
			objectiveId: this.objective.key
		});
		this.currentDegreeType = null;
	}

	showSelection(degree: DegreeType): void {
		this.currentDegreeType = degree;
	}

	removeTag(unit: UnitObjectiveModel): void {
		this.onEditObjective();
		const index: number = this.unitObjectivs.findIndex((o) => o.unitId === unit.unitId);
		this.unitObjectivs.splice(index, 1);
	}

	setLoading(isLoading: boolean) {
		this.isLoading = isLoading;
	}

	setObjective(objective) {
		this.$emit("objective", objective);
	}

	setObjectiveInComponents(objectiveInComponents) {
		this.objectiveInComponents = objectiveInComponents;
	}

	setObjectiveUnitList(unitObjectiveList) {
		this.objectiveUnitList = unitObjectiveList;
	}

	deleteObjectiveApi() {
		return ObjectiveService.deleteObjective(this.objective.id);
	}

	deleteObjectiveCallback() {
		if (!this.canNotDelete) {
			ObjectiveModule.setCurrentActionType(ObjectiveActionType.Delete);
			this.deleteObjectiveApi()
				.then(() => {
					this.onClose();
				})
				.catch(({ error_code }) => {
					if (error_code == ServerExceptionCode.TargetHasExistedException) {
						MessageHelper.Message(NotificationType.Failed, fmtMsg(VcsLocale.ObjectiveHasChildNodeTip));
					}
				});
		}
	}

	deleteObjective() {
		this.operateTreeNodes(this.treedata, this.objective);
		Modal.confirm({
			class: "delete-objective",
			title: "Confirmation",
			content: this.deleteMsg,
			okText: fmtMsg(CommonLocale.CommonOkButtonText),
			cancelText: fmtMsg(CommonLocale.CommonCancelButtonText),
			onOk: () => {
				this.deleteObjectiveCallback();
			},
			onCancel: () => {}
		});
	}

	operateTreeNodes(array, objective) {
		const currentNode = array.find((a) => a.id === objective.id);
		if (currentNode) {
			if (currentNode.children.length) {
				this.canNotDelete = true;
				this.deleteMsg = fmtMsg(VcsLocale.ObjectiveHasChildNodeTip);
			} else if (
				(currentNode.level == 3 && this.unitObjectivs.length) ||
				(currentNode.level == 4 && this.relatedContents.length) ||
				(currentNode.level == 4 && this.selectedUnitOptions.length)
			) {
				this.canNotDelete = false;
				this.deleteMsg = fmtMsg(VcsLocale.ObjectiveHasContentResourceTip);
			} else {
				this.canNotDelete = false;
				this.deleteMsg = fmtMsg(VcsLocale.ObjectiveCanDeleteTip);
			}
		} else {
			array.forEach((a) => {
				if (a.children && a.children.length) {
					this.operateTreeNodes(a.children, objective);
				}
			});
		}
	}

	@Watch("visible")
	clearFormFields(value) {
		if (!value) {
			this.unitObjectivs = [];
			this.selectedUnitOptions = [];
			this.parentUnitOptions = [];
			this.relatedContents = [];
			this.contentObjectives = [];
			this.currentDegreeType = null;
			this.unitManageForm.resetFields();
			this.filteredContentObjectives = [];
		}
	}

	onClose() {
		this.$emit("visible", false);
	}

	validateInputCustomText(rule, value, callback) {
		const formatValue = pureRichText(value);
		if (formatValue && !formatValue.trim()) {
			callback(fmtMsg(CommonLocale.CommonFieldRequired, { name: "text" }));
			return;
		}
		callback();
	}

	public validateTitle(rule, value, callback) {
		if (value && !value.trim()) {
			callback(fmtMsg(CommonLocale.CommonFieldRequired, { name: "text" }));
			return;
		}
		callback();
	}

	showErrorMessage(error_code, error_description) {
		this.setLoading(false);

		const errorDescriptionPrefix = "Error:";
		const contentData =
			error_description.length > 0 ? error_description[0].replace(errorDescriptionPrefix, "") : "";

		switch (error_code) {
			case ServerExceptionCode.TargetHasExistedException:
				MessageHelper.Message(NotificationType.Warning, fmtMsg(VcsLocale.ObjectiveTextHasExisted));
				return false;
			case ServerExceptionCode.InvalidObjectiveRemovalException:
				this.setObjectiveUnitList(JSON.parse(contentData));
				this.setObjectiveInComponents(true);
				return false;
			default:
				return true;
		}
	}

	getLevel4UnitObjectives() {
		const unitObjectives: any[] = [];

		this.selectedUnitOptions.forEach((u) => {
			const unitObjective = {
				degree: DegreeType.Expert,
				unitId: u
			};

			if (ObjectiveModule.currentActionType == ObjectiveActionType.Edit) {
				Object.assign(unitObjective, { objectiveId: this.objective.key });

				const existingUnitObjective = ObjectiveModule.ownunitobjectiverelated
					.filter((un) => un.unit.curriculumType === this.versionGroup.curriculumType)
					.find((uo) => uo.unitId == u);
				if (!isUndefined(existingUnitObjective)) {
					Object.assign(unitObjective, { id: existingUnitObjective.id });
				}
			}
			unitObjectives.push(unitObjective);
		});

		return unitObjectives;
	}

	createObjective(values) {
		const model = {
			text: values.title,
			parentId: this.objective.id,
			versionGroupId: this.versionGroup.id,
			level: this.objective.level ? this.objective.level + 1 : 1,
			position: this.objectiveInsertPosition,
			sequence: 0
		};
		this.objective.level == 2 && Object.assign(model, { unitObjective: this.unitObjectivs });
		this.objective.level == 3 && Object.assign(model, { unitObjective: this.getLevel4UnitObjectives() });

		ObjectiveService.createObjective(model)
			.then((data) => {
				this.setObjective({
					...model,
					...data,
					key: data.id,
					title: values.title,
					show: false,
					children: [],
					scopedSlots: { title: "custom" }
				});
				this.setLoading(false);
				this.onClose();
			})
			.catch(({ error_code, error_description }) => {
				this.showErrorMessage(error_code, error_description);
			});
	}

	renderLevel4WarningList(h: any, objetiveUnitList: ObjectiveUnitListModel[]) {
		return objetiveUnitList.map(({ Title, DisplayName }) =>
			h("ul", null, [
				h("li", null, [
					h("span", DisplayName),
					...Title.map((componentTitle) => h("ul", null, [h("li", componentTitle)]))
				])
			])
		);
	}

	displayLevel4WarningMessage(h: any) {
		return h("div", null, [
			h("div", null, [h("p", fmtMsg(VcsLocale.ContentObjectiveUnitWarning))]),
			h("div", null, [this.objectiveUnitList.length && this.renderLevel4WarningList(h, this.objectiveUnitList)])
		]);
	}

	renderLevel3WarningList(h: any, filteredContentObjectives: UnitobjectiverelatedModel[]) {
		return filteredContentObjectives.map((unit) =>
			h("ul", null, [
				h("li", null, [
					h("span", unit.unit.displayName),
					...unit.contents.map(({ title }) => h("ul", null, [h("li", title)]))
				])
			])
		);
	}

	displayLevel3WarningMessage(h: any) {
		return h("div", null, [
			h("div", null, [h("p", fmtMsg(VcsLocale.ContentObjectiveUnitWarning))]),
			h("div", null, [
				this.filteredContentObjectives.length && this.renderLevel3WarningList(h, this.filteredContentObjectives)
			])
		]);
	}

	handleOk() {
		this.setLoading(false);
		this.onClose();
	}

	showWarningModal() {
		Modal.warning({
			title: fmtMsg(VcsLocale.ContentObjectiveUnitTitle),
			content: this.isLevel3Warning ? this.displayLevel3WarningMessage : this.displayLevel4WarningMessage,
			onOk: () => {
				this.isLevel3Warning ? this.handleOk() : {};
			}
		});
	}

	objectivesWithCount() {
		return this.contentObjectives.filter(({ count }) => count > 0);
	}

	comparingObjectiveLists(filteredContentObjectives, newUnitObjectives) {
		const res = filteredContentObjectives.every((x) => newUnitObjectives.some((y) => x.unitId === y.unitId));
		return res;
	}

	editObjective(values) {
		const model = {
			text: values.title,
			id: this.objective.id,
			parentId: this.objective.parentId,
			versionGroupId: this.versionGroup.id,
			level: this.objective.level,
			unitObjective: this.objective.level === 4 ? this.getLevel4UnitObjectives() : this.unitObjectivs,
			position: this.objectiveEditPosition
		};

		const res = this.comparingObjectiveLists(this.filteredContentObjectives, model.unitObjective);
		if (!res) {
			this.filteredContentObjectives = this.filteredContentObjectives.filter(
				(x) => !model.unitObjective.some((y) => y.unitId === x.unitId)
			);
			this.isLevel3Warning = true;
			this.showWarningModal();
		} else {
			ObjectiveService.updateObjective(model)
				.then((retData) => {
					retData.forEach((data) => {
						this.setObjective({ ...data, key: data.id, title: data.text });
					});
					this.setLoading(false);
					this.onClose();
				})
				.catch(({ error_code, error_description }) => {
					this.showErrorMessage(error_code, error_description);
					if (this.objectiveInComponents) {
						this.showWarningModal();
						ObjectiveModule.getUnitobjectiverelatedwithparent(model);
					}
				});
		}
	}

	onEditObjective() {
		this.enableSaveButton = true;
	}

	onSubmit() {
		this.setLoading(true);
		this.unitManageForm.validateFields((err, values) => {
			if (!err) {
				values.title = values.title.trim();
				if (ObjectiveModule.currentActionType == ObjectiveActionType.New) {
					this.createObjective(values);
				} else if (ObjectiveModule.currentActionType == ObjectiveActionType.Edit) {
					this.filteredContentObjectives = this.objectivesWithCount();
					this.editObjective(values);
				}
			} else {
				this.setLoading(false);
			}
		});
	}

	handlePositionChange(value) {
		this.onEditObjective();
		if (this.objective.isObjectiveIdNew) {
			this.objectiveInsertPosition = value;
		} else {
			this.objectiveEditPosition = value;
		}
	}

	get maxPosition() {
		if (this.objective.isObjectiveIdNew && !this.objective.children) {
			return this.treedata.length + 1;
		}

		return this.objective.children.length + 1;
	}

	get currentPosition() {
		if (this.objective && this.objective.index) {
			return Number(
				this.objective.index
					.split(".")
					.filter((idx) => idx !== "")
					.pop()
			);
		}
		return 0;
	}

	get objectiveLevelsCount() {
		let objectivesCount;
		this.treedata.forEach((level1Data) => {
			if (level1Data.id === this.objective.parentId && level1Data.level === this.objective.level - 1) {
				objectivesCount = level1Data.children.length;
			} else {
				level1Data.children.forEach((level2Data) => {
					if (level2Data.id === this.objective.parentId && level2Data.level === this.objective.level - 1) {
						objectivesCount = level2Data.children.length;
					} else {
						level2Data.children.forEach((level3Data) => {
							if (
								level3Data.id === this.objective.parentId &&
								level3Data.level === this.objective.level - 1
							) {
								objectivesCount = level3Data.children.length;
							}
						});
					}
				});
			}
		});

		return objectivesCount;
	}
}
